'use client';

import { Flex, Radio } from '@/_components';
import type { QuestionFormRadioProps } from './QuestionFormRadio.types';
import {
  useContext,
  useState,
  useMemo,
  type FC,
  useCallback,
  useEffect,
} from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { getHasRolloverItem } from '@/_utils/questions';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { Question } from '@/_types/models/questionnaire';
import { useDebounce } from '@/_hooks/useDebounce';
import { SAVE_TOGGLE_DELAY_MS } from '@/_constants/questions';

const DEFAULT_QUESTION_OPTIONS: Array<string> = ['Yes', 'No'] as const;

export const QuestionFormRadio: FC<QuestionFormRadioProps> = ({
  question,
  isDisabled,
  onChange,
  mode,
}) => {
  const { updateQuestion, selectedQuestions, selectedQuestionnaire } =
    useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion, previewQuestions } =
    useContext(PreviewContext);
  const { updateTemplateQuestion, previewTemplate } =
    useContext(UserPreviewContext);

  const [answerToDebounce, setAnswerToDebounce] = useState<
    | {
        questionId: number;
        answer: string;
      }
    | undefined
  >(undefined);

  const debouncedAnswer = useDebounce(answerToDebounce, SAVE_TOGGLE_DELAY_MS);

  const answerQuestion = useCallback(
    async (answerParams: { questionId: number; answer: string }) => {
      if (mode === 'admin-preview') {
        await updatePreviewQuestion({ ...answerParams });
      } else if (mode === 'admin-template') {
        await updateTemplateQuestion(answerParams);
      } else {
        await updateQuestion({ ...answerParams, isAsync: true });
      }
    },
    [mode, updateQuestion, updateTemplateQuestion, updatePreviewQuestion],
  );

  useEffect(() => {
    if (debouncedAnswer) {
      answerQuestion(debouncedAnswer);
    }
  }, [debouncedAnswer]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAnswer = async (answer: string) => {
    const answerParams = { questionId: question.id, answer };

    onChange?.(answer);
    setAnswerToDebounce(answerParams);
  };

  const questions: Array<Question> = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestions;
    if (mode === 'admin-template') {
      return previewTemplate?.questionnaire_questions || [];
    }

    return selectedQuestions;
  }, [
    mode,
    previewQuestions,
    selectedQuestions,
    previewTemplate?.questionnaire_questions,
  ]);

  const questionOptions: Array<string> = useMemo(
    () =>
      question.question_type === 'boolean'
        ? DEFAULT_QUESTION_OPTIONS
        : question.options || [],
    [question?.options, question?.question_type],
  );

  return (
    <Flex isColumn={true} gap={4}>
      {questionOptions?.map((option, key) => {
        const onRadioChange = async () => {
          onChange?.(option);
          await onAnswer(option);
        };

        const hasRolloverItem: boolean = getHasRolloverItem(
          questions,
          question.id,
          option,
        );

        const previousYear: string = selectedQuestionnaire?.tax_year
          ? `in ${(selectedQuestionnaire?.tax_year ?? 0) - 1}`
          : 'last year';
        const pillText: string | undefined = hasRolloverItem
          ? mode === 'admin-preview'
            ? `Client had this ${previousYear}`
            : `You had this ${previousYear}`
          : undefined;

        return (
          <Radio
            key={key}
            name={`question-form-radio-${question?.id}`}
            isDisabled={isDisabled}
            selectedValue={option}
            value={question?.answer || ''}
            pillText={pillText}
            label={option}
            onChange={onRadioChange}
            withGlossary={true}
          />
        );
      })}
    </Flex>
  );
};
